/**
 * React bindings for the outbox and for connectivity.
 *
 * Kept out of the components so the components have no IndexedDB knowledge, and so the subscription
 * lifetime is in one place — an outbox subscription leaked on unmount would keep calling `setState`
 * on a dead component every time the queue changes.
 */

import { useEffect, useState } from 'react';
import { countByState, subscribe, type OutboxCounts, type OutboxItem } from '../core/outbox';
import { assessStorage, type StorageAssessment } from '../core/storage';

export function useOutbox(): { items: readonly OutboxItem[]; counts: OutboxCounts } {
  const [items, setItems] = useState<readonly OutboxItem[]>([]);
  useEffect(() => subscribe(setItems), []);
  return { items, counts: countByState(items) };
}

/**
 * `navigator.onLine` plus the two events.
 *
 * ⚠️ `navigator.onLine === true` means "there is a network interface", NOT "the server is reachable".
 * A captive portal, a VPN mid-handshake, or a depot wifi with no uplink all report `true`. So this
 * hook drives the BANNER only — the sync engine never decides success from it, it decides from what
 * happened to an actual request. A UI that said "online" while every submission failed would be
 * lying, so the banner text below is deliberately about the device rather than about the service.
 */
export function useOnline(): boolean {
  const [online, setOnline] = useState<boolean>(() => globalThis.navigator?.onLine !== false);
  useEffect(() => {
    const up = (): void => setOnline(true);
    const down = (): void => setOnline(false);
    globalThis.addEventListener('online', up);
    globalThis.addEventListener('offline', down);
    return () => {
      globalThis.removeEventListener('online', up);
      globalThis.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

/** Asks for persistent storage once, on mount, and exposes the honest verdict. */
export function useStorageAssessment(): StorageAssessment | null {
  const [assessment, setAssessment] = useState<StorageAssessment | null>(null);
  useEffect(() => {
    let live = true;
    void assessStorage().then((result) => {
      if (live) {
        setAssessment(result);
      }
    });
    return () => {
      live = false;
    };
  }, []);
  return assessment;
}
